import { tr } from "./localization";
import { createContext, useContext, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus, Inbox, X } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "./api";
import type { Board, Task, TaskList, User } from "./types";
import { initials, priorities } from "./utils";

export type Ctx = {
  me: User;
  users: User[];
  boards: Board[];
  openTask: (id: string, replace?: boolean) => void;
  create: (prefill?: string, boardId?: string, columnId?: string) => void;
  refresh: () => void;
};

export const Context = createContext<Ctx>(null!);

export const useApp = () => useContext(Context);

export const err = (e: unknown, retry?: () => void) =>
  toast.error(
    e instanceof Error
      ? e.message
      : tr("Не удалось сохранить. Повторите действие"),
    {
      action:
        retry && !(e instanceof ApiError && e.status === 409)
          ? { label: tr("Повторить"), onClick: retry }
          : undefined,
    },
  );

export const Avatar = ({ user }: { user?: User }) => (
  <span
    className={
      "avatar c" +
      (user
        ? (Array.from(user.id).reduce((n, c) => n + c.charCodeAt(0), 0) % 6) + 1
        : 6)
    }
    title={user?.name || tr("Не назначен")}
  >
    {user ? initials(user.name) : "—"}
  </span>
);

export const userName = (users: User[], id: string | null) =>
  users.find((u) => u.id === id)?.name || tr("Не назначен");

export const Button = ({
  children,
  onClick,
  disabled = false,
  className = "",
  type = "button",
  ...rest
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  [key: string]: unknown;
}) => (
  <button
    type={type}
    className={"btn " + className}
    onClick={onClick}
    disabled={disabled}
    {...rest}
  >
    {children}
  </button>
);

export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
  className = "",
  subtitle,
  focusContainer = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  focusContainer?: boolean;
  children: ReactNode;
  wide?: boolean;
  className?: string;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay" />
        <Dialog.Content
          className={"dialog " + (wide ? "wide " : "") + className}
          onOpenAutoFocus={
            focusContainer
              ? (event) => {
                  event.preventDefault();
                  (event.target as HTMLElement).focus();
                }
              : undefined
          }
        >
          <header>
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              {subtitle && <p className="dialog-sub">{subtitle}</p>}
            </div>
            <Dialog.Close className="btn icon" aria-label={tr("Закрыть")}>
              <X size={18} />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            {tr("Заполните поля и подтвердите действие")}
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const nameFromEmail = (email: string) =>
  email
    .split("@")[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ") || email;

export function Loading() {
  return (
    <div className="loading" role="status" aria-label={tr("Загрузка")}>
      <div />
      <div />
      <div />
    </div>
  );
}

export function Empty({
  text = tr("Задач пока нет"),
  description = tr("Здесь появятся ваши рабочие задачи."),
  showCreate = true,
}: {
  text?: string;
  description?: string;
  showCreate?: boolean;
}) {
  const ctx = useContext(Context);
  return (
    <div className="empty">
      <Inbox size={36} />
      <h3>{text}</h3>
      <p>{description}</p>
      {ctx && showCreate && (
        <Button className="primary" onClick={() => ctx.create()}>
          <Plus size={16} />
          {tr("Создать задачу")}
        </Button>
      )}
    </div>
  );
}

export function ErrorState({
  error,
  retry,
}: {
  error: unknown;
  retry: () => void;
}) {
  return (
    <div className="empty" role="alert">
      <h3>
        {error instanceof ApiError && error.status === 404
          ? tr("Задача не найдена")
          : error instanceof Error
            ? error.message
            : tr("Не удалось загрузить данные")}
      </h3>
      <p>{tr("Проверьте подключение и повторите попытку.")}</p>
      <Button onClick={retry}>{tr("Повторить")}</Button>
    </div>
  );
}

export async function perform(
  task: Task,
  action: string,
  refresh: () => void,
  data: Record<string, unknown> = {},
): Promise<Task | undefined> {
  try {
    const res = await api<{ task: Task; undo_token: string | null }>(
      `/tasks/${task.id}/actions`,
      "POST",
      { version: task.version, action, ...data },
    );
    refresh();
    toast.success(
      action === "submit"
        ? res.task.kind === "review"
          ? tr("Сдано на проверку")
          : tr("Задача выполнена")
        : tr("Изменения сохранены"),
      res.undo_token
        ? {
            action: {
              label: tr("Отменить"),
              onClick: async () => {
                try {
                  await api("/undo/" + res.undo_token, "POST");
                  refresh();
                  toast.success(tr("Действие отменено"));
                } catch (e) {
                  err(e);
                  refresh();
                }
              },
            },
          }
        : {},
    );
    return res.task;
  } catch (e) {
    if (
      e instanceof ApiError &&
      e.code === "checklist_incomplete" &&
      !data.confirm_incomplete_checklist
    ) {
      if (window.confirm(e.message))
        return perform(task, action, refresh, {
          ...data,
          confirm_incomplete_checklist: true,
        });
      return;
    }
    err(e);
    refresh();
  }
}

export function PriorityIcon({
  priority,
  label = false,
}: {
  priority: string;
  label?: boolean;
}) {
  return (
    <span
      className={"priority " + priority}
      role="img"
      title={priorities[priority]}
      aria-label={priorities[priority]}
    >
      <span className="priority-bars" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label && <span>{priorities[priority]}</span>}
    </span>
  );
}

export function ReassignmentDialog({
  title,
  personId,
  tasks,
  leaveValue,
  close,
  submit,
}: {
  title: string;
  personId: string;
  tasks: Task[];
  leaveValue: "leave" | "guest";
  close: () => void;
  submit: (
    decision: string,
    replacement: string,
    addToBoards: boolean,
  ) => Promise<void>;
}) {
  const { users } = useApp();
  const [decision, setDecision] = useState<string>(leaveValue),
    [replacement, setReplacement] = useState(""),
    [add, setAdd] = useState(false),
    [busy, setBusy] = useState(false);
  return (
    <Modal open title={title} onClose={close}>
      <p>
        {tr("Открытых задач:")}
        {tasks.length}
      </p>
      <ul>
        {tasks.map((t) => (
          <li key={t.id}>
            {t.title} · {t.board_name}
          </li>
        ))}
      </ul>
      <label>
        {tr("Что сделать с задачами")}
        <select value={decision} onChange={(e) => setDecision(e.target.value)}>
          <option value={leaveValue}>
            {leaveValue === "guest"
              ? tr("Оставить исполнителем с доступом гостя")
              : tr("Оставить текущего исполнителя")}
          </option>
          <option value="reassign">{tr("Переназначить")}</option>
          <option value="unassign">{tr("Снять исполнителя")}</option>
        </select>
      </label>
      {decision === "reassign" && (
        <label>
          {tr("Новый исполнитель")}
          <select
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
          >
            <option value="">{tr("Выберите сотрудника")}</option>
            {users
              .filter((u) => u.id !== personId && u.is_active !== false)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </select>
        </label>
      )}
      {leaveValue === "leave" && (
        <>
          <p className="muted">
            {tr("Явное членство в прежних досках сохранится.")}
          </p>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={add}
              onChange={(e) => setAdd(e.target.checked)}
            />
            {tr("Добавить участником в доски нового отдела")}
          </label>
        </>
      )}
      <footer>
        <Button onClick={close}>{tr("Отмена")}</Button>
        <Button
          className="primary"
          disabled={busy || (decision === "reassign" && !replacement)}
          onClick={async () => {
            setBusy(true);
            try {
              await submit(decision, replacement, add);
            } catch (e) {
              err(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          {tr("Подтвердить")}
        </Button>
      </footer>
    </Modal>
  );
}

export async function loadTasks(path: string) {
  let offset = 0;
  const items: Task[] = [];
  for (;;) {
    const res = await api<TaskList>(
      path + (path.includes("?") ? "&" : "?") + "limit=200&offset=" + offset,
    );
    items.push(...res.items);
    if (res.next_offset === null) return items;
    offset = res.next_offset;
  }
}
